'use client';

import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { jsPDF } from 'jspdf';
import { useLocale } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { useMemo, useRef, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useForm } from 'react-hook-form';
import { localesUsingSpecialSymbols, usePdfFontLoader } from '@module/mdrt-org/dashboard/dashboard-page-content/components/pdf-modal-wrapper/use-pdf-font-loader';
import { type VisaLetterPDFContent } from '@module/mdrt-org/dashboard/dashboard-page-content/components/pdf-modal-wrapper/visa-letters-pdf-modal/get-visa-letter-pdf-content';
import { postEmailWithVisaLetterPdf } from '@module/mdrt-org/dashboard/dashboard-page-content/components/pdf-modal-wrapper/visa-letters-pdf-modal/post-email-with-visa-letter-pdf';
import { Dialog } from '@module/mdrt-org/shared/components/dialog/dialog';
import { FieldController } from '@module/mdrt-org/shared/components/field-controller/field-controller';
import { usePathname, useRouter } from '@module/mdrt-org/shared/i18n/i18n-navigation';
import { MAX_EMAIL_LENGTH } from '@module/mdrt-org/shared/utils/constants/input-constants';
import { HOMEPAGE_PATH } from '@module/mdrt-org/shared/utils/constants/local-paths';
import { VISA_LETTER } from '@module/mdrt-org/shared/utils/constants/pdf-modals-search-constants';
import { type Locale } from '@module/mdrt-org/shared/utils/enums/locale';
import { VisaLetterType } from '@module/mdrt-org/shared/utils/enums/visa-letter-type';
import { formatDateLong } from '@module/mdrt-org/shared/utils/helpers/formatters/date-formatters';
import { useMutationHandler } from '@module/mdrt-org/shared/utils/hooks/use-mutation-handler';
import { type Address } from '@module/mdrt-org/shared/utils/types/customer-profile';
import { EMAIL_REGEXP } from '@module/mdrt-org/shared/utils/types/regexp';
import { Alert } from '@shared/ui-components/alert/alert';
import { AlertVariant } from '@shared/ui-components/alert/alertVariant';
import { Card } from '@shared/ui-components/card/card';
import { CheckboxField } from '@shared/ui-components/checkbox-field/checkbox-field';
import { DialogActions } from '@shared/ui-components/dialog/components/dialog-actions/dialog-actions';
import { FormInput } from '@shared/ui-components/form-input/form-input';
import { FormWrapper } from '@shared/ui-components/form-wrapper/form-wrapper';
import { ModalTrigger } from '@shared/ui-components/modal/modal';
import { RenderHTML } from '@shared/ui-components/render-html/render-html';
import { GENERIC_ERROR_MESSAGE_FALLBACK, GENERIC_ERROR_TRANSLATION_KEY } from '@shared/utils/constants/errors';
import { formBaseConfig } from '@shared/utils/constants/formBaseConfig';
import { ButtonType } from '@shared/utils/enums/button-type';
import { useFormatTranslationString } from '@shared/utils/helpers/format-translation-string';
import { useCssVariable } from '@shared/utils/hooks/use-css-variable';
import styles from './visa-letter-pdf-modal.module.scss';
const replaceTemplateVariables = (html: string, variables: Record<string, number | string | undefined>): string => html.replaceAll(/\{\{\s*(\w+)\s*\}\}/gu, (match, key) => {
  return key in variables && variables[key] !== undefined ? String(variables[key]) : match;
});
const JSPDF_WINDOW_WIDTH = 800;
export type VisaLetterPdfModalProps = {
  address: Nullable<Address>;
  emailAddress: Nullable<string>;
  fullName: string;
  visaLettersCmsContent: VisaLetterPDFContent;
};
type VisaLetterPdtFormData = {
  email: string;
  shouldSendByEmail: boolean;
};
export const VisaLetterPdfModal = ({
  address,
  emailAddress,
  fullName,
  visaLettersCmsContent
}: VisaLetterPdfModalProps) => {
  const formatTranslationString = useFormatTranslationString();
  const locale = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const searchParameters = useSearchParams();
  const pdfContentRef = useRef(null);
  const [PDF_FONT_FAMILY] = useCssVariable('--pdf-font-family');
  const modalType = searchParameters.get(VISA_LETTER);
  const [hasReadTerms, setHasReadTerms] = useState(false);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [isPDFGenerating, setIsPDFGenerating] = useState(false);
  const {
    boldFontBinaryString,
    isLoading: isFontLoading,
    regularFontBinaryString
  } = usePdfFontLoader();
  const addressNode = address && <>
      {address.line1 && <div>{address.line1}</div>}
      {address.line2 && <div>{address.line2}</div>}
      {address.line3 && <div>{address.line3}</div>}
      {address.line4 && <div>{address.line4}</div>}
      {(address.city || address.stateProvince || address.postalCode) && <div>
          {address.city && <span>{address.city}</span>}
          {address.stateProvince && <span>, {address.stateProvince}</span>}
          {address.postalCode && <span> {address.postalCode}</span>}
        </div>}
      {address.country && <div>{address.country}</div>}
    </>;
  const invitationMap = {
    [VisaLetterType.GlobalConference]: visaLettersCmsContent.globalConferenceMeetingInvitation,
    [VisaLetterType.MDRTAnnualMeeting]: visaLettersCmsContent.mdrtAnnualMeetingInvitation,
    [VisaLetterType.TOTAnnualMeeting]: visaLettersCmsContent.totMeetingInvitation
  };
  const isVisaLetterModalType = modalType === VisaLetterType.GlobalConference || modalType === VisaLetterType.MDRTAnnualMeeting || modalType === VisaLetterType.TOTAnnualMeeting;
  const selectedInvitation = isVisaLetterModalType && modalType !== null ? invitationMap[modalType] : undefined;
  const pdfDocumentData = selectedInvitation ? {
    BusinessAddressPlaceholder: selectedInvitation.businessAddress,
    BusinessContactInformationPlaceholder: selectedInvitation.businessContactInformation,
    DatePlaceholder: formatDateLong(new Date(), locale),
    GreetingPlaceholder: formatTranslationString(selectedInvitation.letterGreeting ?? '', {
      FirstAndLastName: fullName
    }),
    LetterClosingPlaceholder: selectedInvitation.letterClosing,
    MainContentPlaceholder: selectedInvitation.mainContentPart,
    MDRTLogoImagePlaceholder: selectedInvitation.mdrtLogoImageUrl,
    OrganizerInformationLabelPlaceholder: selectedInvitation.organizerInformationLabel ?? undefined,
    OrganizerInformationPlaceholder: selectedInvitation.organizerInformation ?? undefined,
    PresidentNamePlaceholder: visaLettersCmsContent.presidentData.presidentNameWithDesignations,
    PresidentTitlePlaceholder: selectedInvitation.presidentsTitle,
    SignatureImageLinkPlaceholder: visaLettersCmsContent.presidentData.signatureImageUrl,
    UserAddressPlaceholder: addressNode === null ? undefined : renderToStaticMarkup(<div>{addressNode}</div>),
    UserNamePlaceholder: fullName
  } : {};
  const {
    data: htmlTemplate,
    isLoading: isHtmlTemplateLoading
  } = useQuery({
    enabled: modalType !== undefined && selectedInvitation?.templateUrl !== undefined,
    queryFn: async () => {
      if (selectedInvitation?.templateUrl === undefined) {
        throw new Error('Url is not defined');
      }
      const response = await fetch(selectedInvitation?.templateUrl);
      if (!response.ok) throw new Error('Failed to fetch template');
      return response.text();
    },
    queryKey: [selectedInvitation?.templateUrl]
  });
  const [downloadStepErrorMessage, setDownloadStepErrorMessage] = useState<string | undefined>();
  const handleModalClose = () => {
    const params = new URLSearchParams(searchParameters.toString());
    params.delete(VISA_LETTER);
    const query = params.toString();
    router.replace(query ? `${pathname}?${query}` : pathname, {
      scroll: false
    });
  };
  const {
    isMutationPending,
    mutate
  } = useMutationHandler({
    mutationFunction: postEmailWithVisaLetterPdf,
    mutationKey: [],
    onFailure: result => {
      setDownloadStepErrorMessage(result.messageTranslationKey === GENERIC_ERROR_TRANSLATION_KEY && visaLettersCmsContent.generalModalContent.errorMessage ? visaLettersCmsContent.generalModalContent.errorMessage : result.message);
    },
    onSuccessHandler: () => {
      handleModalClose();
    },
    showSuccessToast: true,
    successMessage: visaLettersCmsContent.generalModalContent.emailWasQueuedSuccessfullyMessage
  });
  const isLoading = useMemo(() => isHtmlTemplateLoading || isPDFGenerating || isMutationPending || isFontLoading, [isFontLoading, isHtmlTemplateLoading, isMutationPending, isPDFGenerating]);
  const formMethods = useForm<VisaLetterPdtFormData>({
    ...formBaseConfig,
    defaultValues: {
      email: emailAddress ?? '',
      shouldSendByEmail: false
    }
  });
  const {
    resetField,
    watch
  } = formMethods;
  const watchSendByEmailCheckbox = watch('shouldSendByEmail');
  const handleSubmit = async (formData: VisaLetterPdtFormData) => {
    if (pdfContentRef.current === null) {
      return;
    }
    setIsPDFGenerating(true);
    const pdfDocument = new jsPDF();
    try {
      if (regularFontBinaryString && boldFontBinaryString) {
        const vfsRegularFontName = 'Noto-Sans-Regular.ttf';
        const vfsBoldFontName = 'Noto-Sans-Bold.ttf';
        pdfDocument.addFileToVFS(vfsRegularFontName, regularFontBinaryString);
        pdfDocument.addFileToVFS(vfsBoldFontName, boldFontBinaryString);
        pdfDocument.addFont(vfsRegularFontName, PDF_FONT_FAMILY.propertyValue, 'normal');
        pdfDocument.addFont(vfsBoldFontName, PDF_FONT_FAMILY.propertyValue, 'bold');
        pdfDocument.setFont(PDF_FONT_FAMILY.propertyValue);
      } else {
        pdfDocument.setFont('helvetica');
      }
      await pdfDocument.html(pdfContentRef.current, {
        async callback(document) {
          const pdfDataUri = document.output('datauristring');
          const encodedPdf = pdfDataUri.split(',')[1];
          document.save('MDRT_meeting_invitation');
          if (formData.shouldSendByEmail) {
            mutate({
              encodedPdf,
              recipientEmail: formData.email,
              recipientName: fullName,
              visaLetterType: modalType
            });
            setIsPDFGenerating(false);
            return;
          }
          setIsPDFGenerating(false);
          handleModalClose();
        },
        width: 210,
        // A4 width in mm
        windowWidth: JSPDF_WINDOW_WIDTH
      });
    } catch {
      setDownloadStepErrorMessage(GENERIC_ERROR_MESSAGE_FALLBACK);
    } finally {
      setIsPDFGenerating(false);
    }
  };
  const sharedModalProps = {
    hasCloseButton: true,
    onModalClose: handleModalClose,
    title: selectedInvitation?.modalTitle
  };
  return isVisaLetterModalType ? <ModalTrigger isOpen={pathname === HOMEPAGE_PATH && modalType !== null} onOpenChange={open => !open && handleModalClose()}>
      {() => hasAcceptedTerms ? <FormWrapper className={styles['visa-letter-pdf-modal-download-form']} dataTestId="visa-letter-generation-form" formMethods={formMethods} onSubmit={handleSubmit}>
            <Dialog {...sharedModalProps} className={styles['visa-letter-pdf-modal-download']} footer={<DialogActions onSecondaryButtonClick={handleModalClose} primaryButtonLabel={visaLettersCmsContent.generalModalContent.downloadButtonLabel} primaryButtonType={ButtonType.SUBMIT} secondaryButtonLabel={visaLettersCmsContent.generalModalContent.cancelButtonLabel} />} isLoading={isLoading}>
              {downloadStepErrorMessage && <Alert className={styles['visa-letter-pdf-modal-download__message']} message={downloadStepErrorMessage} variant={AlertVariant.Error} />}
              <RenderHTML html={visaLettersCmsContent.generalModalContent.generateStepContent} />
              {address && <Card className={styles['visa-letter-pdf-modal-download__address-card']}>
                  <div className={styles['visa-letter-pdf-modal-download__address-card-content']}>
                    {addressNode}
                  </div>
                </Card>}
              <CheckboxField label={visaLettersCmsContent.generalModalContent.sendByEmailCheckboxLabel} name="shouldSendByEmail" onChange={() => resetField('email')} wrapperClassName={styles['visa-letter-pdf-modal-download__input']} />
              {watchSendByEmailCheckbox === true && <FieldController label={visaLettersCmsContent.generalModalContent.emailFieldLabel} maxLength={MAX_EMAIL_LENGTH} name="email" rules={{
          pattern: {
            message: visaLettersCmsContent.generalModalContent.invalidEmailValidationMessage,
            value: EMAIL_REGEXP
          },
          required: visaLettersCmsContent.generalModalContent.requiredLabel
        }}>
                  <FormInput className={styles['visa-letter-pdf-modal-download__input']} type="email" />
                </FieldController>}
              <div aria-hidden className={styles['pdf-document-content-container']}>
                {!isLoading && <div className={classNames(styles['pdf-document-content'], {
            [styles['pdf-document-content--locale-adjusted-font']]: localesUsingSpecialSymbols.includes(locale as Locale)
          })}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: htmlTemplate ? replaceTemplateVariables(htmlTemplate, pdfDocumentData) : ''
          }} ref={pdfContentRef} />}
              </div>
            </Dialog>
          </FormWrapper> : <Dialog {...sharedModalProps} className={styles['visa-letter-pdf-modal-agreement']} footer={<DialogActions onPrimaryButtonClick={() => {
      setHasAcceptedTerms(true);
    }} onSecondaryButtonClick={handleModalClose} primaryButtonDisabled={!hasReadTerms} primaryButtonLabel={visaLettersCmsContent.generalModalContent.continueButtonLabel} secondaryButtonLabel={visaLettersCmsContent.generalModalContent.cancelButtonLabel} />} onScrollToBottom={() => {
      setHasReadTerms(true);
    }}>
            <Alert className={styles['visa-letter-pdf-modal-agreement__message']} message={visaLettersCmsContent.generalModalContent.infoMessage} variant={AlertVariant.Informational} />
            <RenderHTML html={visaLettersCmsContent.generalModalContent.acknowledgeStepContent} />
          </Dialog>}
    </ModalTrigger> : null;
};